/* @HEADER
 * @version 1.1.372 | 2026-10-19
 * @purpose AuthenticatedApp's window- and settings-level subscriptions as one hook — the hostBoardNavRef publication, the two window events (space-delete refused, open-task-in-host) and the two Settings toggles the board page re-renders on (board mode, overdue halo) — each unsubscribed on unmount (ZVX-DEV-6029).
 * @sidecar useHostSubscriptions.ts.header.md
 * @layer app | @hex adapter | @ctx navigation
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-7676, ZVX-DEV-7685, ZVX-DEV-6029, ZVX-DEV-1616, ZVX-DEV-3301, ZVX-DEV-3606, ZVX-DEV-2053, ZVX-DEV-1103, ZVX-DEV-1812, ZVX-DEV-7775

import { useEffect, useState } from 'react'
import type { BoardMode } from '@zvenix/tasks'
import type { SpaceView } from '../app/routes'
import type { NavVia } from './useBoardChangeTell'
import { getAppBoardMode, subscribeAppBoardMode } from '../features/settings/appBoardMode'
import { getOverdueHaloEnabled, subscribeOverdueHaloEnabled } from '../features/settings/appOverdueHalo'
import { navigateToTaskInHost, hostBoardNavRef } from '../features/canvas/hostTaskNavigation'
import { SPACE_DELETE_REJECTED_EVENT } from '../lib/db/boardData'

/** The DOM event the command palette dispatches to open a card in the host (ZVX-DEV-2053). */
export const OPEN_TASK_IN_HOST_EVENT = 'open-task-in-host'

export interface UseHostSubscriptionsDeps {
  /** Re-reads the Space list; called when the server refused a Space delete and restored it. */
  refreshGroups: () => void
  /** The navigation setters published to the module-level canvas openTask port (ZVX-DEV-1616). */
  setActiveSpaceId: (id: string | null, via?: NavVia) => void
  setActiveView: (view: SpaceView) => void
  /** ZVX-DEV-7685: hands a space the card to highlight as it opens. */
  setSearchHighlightOnOpen: (taskId: string | null) => void
  /** ZVX-DEV-7775: marks the next arriving card as not a search's. */
  markQuietArrival: (taskId: string | null) => void
}

export interface HostSubscriptions {
  /** ZVX-DEV-1103 — the global UI-mode toggle, live. */
  appBoardMode: BoardMode
  /** ZVX-DEV-1812 — the overdue-halo toggle, live. */
  appOverdueHaloEnabled: boolean
}

/**
 * ZVX-DEV-6029 (campaign 3, App.tsx) — five effects AuthenticatedApp carried
 * inline. Each is a subscription with a teardown; the two toggles are also
 * state the board page re-renders on. Nothing here decides anything — the
 * decisions live where the events are raised; this hook only wires them.
 *
 * Kept as ONE hook rather than five so the host's lifecycle reads in one place
 * and a test can prove every subscription is released on unmount.
 */
export function useHostSubscriptions({ refreshGroups, setActiveSpaceId, setActiveView, setSearchHighlightOnOpen, markQuietArrival }: UseHostSubscriptionsDeps): HostSubscriptions {
  // ZVX-DEV-1616: publish the space/view setters so the module-level
  // navigateToTaskInHost (canvas openTask port) can enter the SPECIFIC space
  // that owns a task's board — instead of landing on the spaces list. Kept in a
  // ref, not context, so the existing `navigateToTask: navigateToTaskInHost`
  // wiring is untouched.
  useEffect(() => {
    hostBoardNavRef.current = { setActiveSpaceId, setActiveView, setSearchHighlightOnOpen, markQuietArrival }
    return () => { hostBoardNavRef.current = null }
  }, [setActiveSpaceId, setActiveView, setSearchHighlightOnOpen, markQuietArrival])

  // ZVX-DEV-3301 / ZVX-DEV-3606 — the server refused a Space delete (403; only
  // the Owner may delete a Space) and `pushSpacesForProject` has just undone
  // the local delete. `ContentDeleteRefusedBanner` renders the message on the
  // same event; this effect keeps only the refresh, which brings the restored
  // Space back on screen.
  useEffect(() => {
    function onSpaceDeleteRejected() {
      refreshGroups()
    }
    window.addEventListener(SPACE_DELETE_REJECTED_EVENT, onSpaceDeleteRejected)
    return () => window.removeEventListener(SPACE_DELETE_REJECTED_EVENT, onSpaceDeleteRejected)
  }, [refreshGroups])

  // ZVX-DEV-2053: the command palette's «Открыть карточку» row action dispatches
  // `open-task-in-host`. Reuse the same robust host flow the canvas openTask
  // port uses — resolve the task's board, mount the SPECIFIC space, open the
  // detail modal — so an open works even when the card's board is not mounted.
  useEffect(() => {
    function onOpenTaskInHost(e: Event) {
      const taskId = (e as CustomEvent<{ taskId: string }>).detail?.taskId
      if (taskId) navigateToTaskInHost(taskId, 'search')
    }
    window.addEventListener(OPEN_TASK_IN_HOST_EVENT, onOpenTaskInHost)
    return () => window.removeEventListener(OPEN_TASK_IN_HOST_EVENT, onOpenTaskInHost)
  }, [])

  // ZVX-DEV-1103 (Wave 1): the global UI-mode toggle in Settings re-renders any
  // open KanbanBoardGroupPage with the new mode. Default is 'expert'.
  const [appBoardMode, setAppBoardModeState] = useState<BoardMode>(getAppBoardMode)
  useEffect(() => subscribeAppBoardMode((mode) => setAppBoardModeState(mode)), [])

  // ZVX-DEV-1812 (ZVX-33): the overdue-halo toggle, threaded into
  // KanbanBoardGroupPage so the package card can show the pulsing ring
  // without importing app-layer code.
  const [appOverdueHaloEnabled, setAppOverdueHaloEnabled] = useState(getOverdueHaloEnabled)
  useEffect(() => subscribeOverdueHaloEnabled(setAppOverdueHaloEnabled), [])

  return { appBoardMode, appOverdueHaloEnabled }
}
